import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type CSSProperties } from "react";
import { IContentTemplateHandle, IComponent, IContentComponent, IRefreshComponentPreviewHandle } from "../../../models/Interfaces";
import { SiteSwitcher } from "../../../services/SiteSwitcher";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsAlt, faCopy, faEdit, faPlusCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import styles from './designer.module.scss';
import { Button, Row } from "react-bootstrap";
import { ContentEditorService } from "../../../services/ContentEditorService";
import React from "react";
import ComponentPreview from "./ComponentPreview";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";
import { useConfirm } from "../../../config/ConfirmProvider";
import { getColumnDropId, parseColumnDropId } from "./designerDnd";

interface IColumnsTemplate {
  VersionId: number;
  SaveInlineComponent: (component: IContentComponent) => Promise<boolean>;
  PageCss?: string;
  SectionId: number;
  Columns: number;
  ContentComponents: Array<IContentComponent> | null;
  PageClass?: string;
  SectionClass?: string;
  SectionAnchor?: string;
  PageStyle?: CSSProperties;
  SectionStyle?: CSSProperties;
  EditComponent: (component: IContentComponent, propertyName?: string) => void;
  EditingComponentId?: number;
  DraftComponentIds?: number[];
  ComponentDrafts?: Record<number, IContentComponent>;
  DuplicateComponent: (component: IContentComponent) => void;
  DuplicatingComponentId?: number;
  OnInlineDirtyChange?: (id: number, dirty: boolean) => void;
  AllComponentsLoaded?: (sectionid: number) => void;
  onComponentsChange: (
    sectionId: number,
    updater: (components: Array<IContentComponent>) => Array<IContentComponent>
  ) => void;
  enqueueLayoutSave: (componentIds: Array<number>, save: () => Promise<unknown>) => void;
}

interface IColumnDropZone {
  className: string;
  columnIndex: number;
  sectionId: number;
  onDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => Promise<void>;
}

const EMPTY_COMPONENTS: Array<IContentComponent> = [];

const ColumnDropZone = ({ className, columnIndex, sectionId, onDragEnter, onDragLeave, onDragOver, onDrop }: IColumnDropZone) => {
  return (
    <div
      className={className}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-colindex={columnIndex}
      data-coltype="COMPONENTDROP"
    >
      <FontAwesomeIcon
        icon={faPlusCircle}
        size="5x"
        className={`${styles.text_muted}`}
        style={{ cursor: 'pointer' }}
        title="Drag a component here..." />
    </div>
  );
};

const ColumnsTemplate = forwardRef<IContentTemplateHandle, IColumnsTemplate>((props, ref) => {
  const siteSwitcherRef = useRef<SiteSwitcher>();
  const contentSvc = useRef<ContentEditorService>();
  const contentComponents = props.ContentComponents ?? EMPTY_COMPONENTS;
  const [loadedComponent, setLoadedComponent] = useState<Array<string>>([]);
  const { requestConfirmation } = useConfirm();

  const previewRefs = useRef<{ [key: string]: IRefreshComponentPreviewHandle | null }>({});
  const onComponentsChangeRef = useRef(props.onComponentsChange);
  onComponentsChangeRef.current = props.onComponentsChange;
  const enqueueLayoutSaveRef = useRef(props.enqueueLayoutSave);
  enqueueLayoutSaveRef.current = props.enqueueLayoutSave;

  useImperativeHandle(ref, () => ({
    refreshComponent(component: IContentComponent) {
      if (!component) {
        return;
      }

      previewRefs.current[`PREVIEW-${component.Id}`]?.refreshPreview();
    },

    async dropSidebarComponent(component: IComponent, dropTargetId: string) {
      const target = parseColumnDropId(dropTargetId);

      if (target && target.sectionId === props.SectionId && contentSvc.current) {
        const res = await contentSvc.current.AddCComponentToColumnSection(component, props.SectionId, target.columnIndex);

        if (res) {
          onComponentsChangeRef.current(props.SectionId, (components) =>
            components.some((item) => item.Id === res.Id) ? components : [...components, res]
          );
          props.EditComponent(res);
          actionEmitter.emit(EmitEvent.ComponentDropped, true, "component dropped in template");
        }
      }
    }
  }));

  const getColClass = (columns: number): string => {
    let cols: string;

    switch (columns) {
      case 1:
      default:
        cols = "col-xs-12 col-md-12";
        break;
      case 2:
        cols = "col-xs-12 col-md-6";
        break;
      case 3:
        cols = "col-xs-12 col-md-4";
        break;
      case 4:
        cols = "col-xs-12 col-md-3";
        break;
      case 6:
        cols = "col-xs-12 col-md-2";
        break;
      case 12:
        cols = "col-xs-12 col-md-1";
        break;
    }
    return cols;
  };

  const getColSpan = (templatecols: number, colspan: number): string => {
    const defcol: number = 12 / templatecols;
    const tospan = defcol * colspan;
    return `col-xs-12 col-md-12 col-lg-${tospan}`;
  };

  const colClass: string = getColClass(props.Columns);

  const onExistingDragStart = (component: IContentComponent, event: React.DragEvent<HTMLDivElement>) => {
    event.dataTransfer.setData('component', JSON.stringify(component));
    event.dataTransfer.setData('dragtype', 'EXISTING');
  };

  const onColDragOver = (event: React.DragEvent<HTMLDivElement>) : void => {
    const dragType = event.dataTransfer.getData("dragtype");
    const hasSidebarDrag = document.body.dataset.sidebarDragging === "true";
    const hasComponentPayload = event.dataTransfer.types.includes("component") || event.dataTransfer.types.includes("text/plain");
    if (dragType === "EXISTING" || dragType === "NEW_COMPONENT" || hasSidebarDrag || hasComponentPayload) {
      event.preventDefault();
      event.dataTransfer.dropEffect = dragType === "NEW_COMPONENT" ? "copy" : "move";
      highlightDropCol(event, true);
    }
  };

  const onColDragEnter = (event: React.DragEvent<HTMLDivElement>)  : void => {
    const dragType = event.dataTransfer.getData("dragtype");
    const hasSidebarDrag = document.body.dataset.sidebarDragging === "true";
    const hasComponentPayload = event.dataTransfer.types.includes("component") || event.dataTransfer.types.includes("text/plain");
    if (dragType === "EXISTING" || dragType === "NEW_COMPONENT" || hasSidebarDrag || hasComponentPayload) {
      event.preventDefault();
      highlightDropCol(event, true);
    }
  };

  const onColDragLeave = (event: React.DragEvent<HTMLDivElement>) : void => {
    const targetElem = event.currentTarget as HTMLDivElement;
    const related = event.relatedTarget as HTMLElement;

    // If related target is null or not a child of the parent div, we are leaving
    if (!related || !targetElem.contains(related)) {
      highlightDropCol(event, false);
    }
  };

  const onColComponentDrop = async (event: React.DragEvent<HTMLDivElement>) : Promise<void> => {
    event.preventDefault();

    if (contentSvc.current) {
      const type = event.dataTransfer.getData("dragtype");

      if (type === "NEW_COMPONENT") {
        const componentData = event.dataTransfer.getData("component");
        const target = event.currentTarget as HTMLDivElement;
        const parsedComponent = componentData ? JSON.parse(componentData) as IComponent : null;
        const targetcolindex = !isNaN(Number(target.dataset.colindex))
          ? Number(target.dataset.colindex)
          : null;

        if (parsedComponent && targetcolindex !== null) {
          const res = await contentSvc.current.AddCComponentToColumnSection(parsedComponent, props.SectionId, targetcolindex);

          if (res) {
            props.onComponentsChange(props.SectionId, (components) =>
              components.some((item) => item.Id === res.Id) ? components : [...components, res]
            );
            props.EditComponent(res);
          }
        }
      }

      if (type === "EXISTING") {
        const target = event.currentTarget as HTMLDivElement;

        if (target.dataset?.coltype === "COMPONENTPREVIEW") {
          const source = JSON.parse(event.dataTransfer.getData("component") ?? "") as IContentComponent;
          const targetid = target?.dataset?.componentid ?? "missingid";
          if (source && !isNaN(Number(targetid))) {
            switchComponents(source.Id, Number(targetid));
          }
        } else {
          moveComponent(event);
        }
      }
    }
    highlightDropCol(event, false);
    actionEmitter.emit(EmitEvent.ComponentDropped, true, "component dropped in template");
    event.stopPropagation();
  };

  const highlightDropCol = (event: React.DragEvent<HTMLDivElement>, on: boolean) : void => {
    const targetElem = event.currentTarget as HTMLDivElement;
    event.preventDefault();
    if (on) {
      targetElem?.classList?.add("col-dragover");
    } else {
      targetElem?.classList?.remove("col-dragover");
    }
  };

  const deleteComponent = async (compid: number) : Promise<void> => {
    if (!contentSvc.current) {
      return;
    }

    const confirmed = await requestConfirmation(
      "Delete this component from the section?<br /><b>This action cannot be undone.</b>",
      "Delete Component"
    );

    if (!confirmed) {
      return;
    }

    const res = await contentSvc.current.DeleteContentComponent(compid);

    if (res) {
      props.onComponentsChange(props.SectionId, (components) => components.filter((component) => component.Id !== compid));
    }
  };

  const componentLoaded = async (apitag: string) : Promise<void> => {
    setLoadedComponent((prev) => {
      const list = [...prev, apitag];
      if (list.length === props.ContentComponents?.length) {
        if (props.AllComponentsLoaded) {
          props.AllComponentsLoaded(props.SectionId);
        }
      }
      return list;
    });
  };

  const editComponentContent = (comp: IContentComponent) : void => {
    props.EditComponent(comp);
  };

  const editOnDoubleClick = (event: React.MouseEvent, component: IContentComponent): void => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest("[data-designer-action='delete']")) {
      return;
    }
    editComponentContent(component);
  };

  const moveComponent = async (event: React.DragEvent<HTMLDivElement>) : Promise<void> => {
    const component = JSON.parse(event.dataTransfer.getData("component")) as IContentComponent;
    if (component && contentSvc.current) {
      const target = event.target as HTMLElement;
      const divcontainer = target.closest("div");

      if (divcontainer) {
        const targetcolindex = !isNaN(Number(divcontainer.dataset.colindex))
          ? Number(divcontainer.dataset.colindex)
          : null;

        if (targetcolindex !== null) {
          const componentId = component.Id;
          const columnIndex = targetcolindex;

          enqueueLayoutSaveRef.current([componentId], async () => {
            const res = await contentSvc.current?.MoveContentComponent(componentId, columnIndex);

            if (res) {
              onComponentsChangeRef.current(props.SectionId, (components) =>
                components.map((item) =>
                  item.Id === componentId
                    ? { ...item, ColIndex: columnIndex }
                    : item
                )
              );
            }
          });
        }
      }
    }
  };

  const switchComponents = (sourcecompid: number, targetcompid: number): void => {
    if (!sourcecompid || !targetcompid || !contentSvc.current) {
      return;
    }

    enqueueLayoutSaveRef.current([sourcecompid, targetcompid], async () => {
      const res = await contentSvc.current?.SwitchColumnComponents(sourcecompid, targetcompid);

      if (!res?.source || !res.target) {
        return;
      }

      onComponentsChangeRef.current(props.SectionId, (components) =>
        components.map((component) => {
          if (component.Id === sourcecompid) {
            return res.source;
          }

          if (component.Id === targetcompid) {
            return res.target;
          }

          return component;
        })
      );
    });
  };

  useEffect(() => {
    if (!siteSwitcherRef.current) {
      siteSwitcherRef.current = new SiteSwitcher();
    }

    if (!contentSvc.current) {
      contentSvc.current = new ContentEditorService();
    }
  }, []);

  const renderColumns = () => {
    const columns = [];

    for (let i: number = 0; i < props.Columns; i++) {
      const comp = contentComponents?.find(c => c.ColIndex === i);

      if (comp && comp.ColIndex === i) {
        columns.push(
          <div key={`COMP-${comp.Id}-${comp.ApiTag}`}
            id={`COMP-${comp.Id}-${comp.ApiTag}`}
            data-coltype="COMPONENTPREVIEW"
            data-componentid={comp.Id}
            tabIndex={0}
            className={`${getColSpan(props.Columns, comp.ColSpan)} col-content col-border m-0 p-0`}
            draggable={true}
            onDrop={onColComponentDrop}
            onDragLeave={(e) => highlightDropCol(e, false)}
            onDragExit={(e) => highlightDropCol(e, false)}
            onDragOver={onColDragOver}
            onDragStart={(e) => { onExistingDragStart(comp, e) }}
            onDoubleClick={(event) => editOnDoubleClick(event, comp)}>

            {/* Content Editor Buttons - shown on hover */}
            <div className="ContentEditorButtons">
              <div className="editorButtons">
                <span className="componentLabel">{comp.DisplayName}</span>
                <FontAwesomeIcon
                  icon={faEdit}
                  className="text-white m-1"
                  title="edit content"
                  onClick={() => editComponentContent(comp)}
                  style={{ cursor: 'pointer' }} />
                <Button variant="link" className="btn-nomin text-white p-0"
                  title="Duplicate component" aria-label={`Duplicate ${comp.DisplayName}`}
                  disabled={props.DuplicatingComponentId !== undefined}
                  onClick={event => { event.stopPropagation(); props.DuplicateComponent(comp); }}>
                  <FontAwesomeIcon icon={faCopy} />
                </Button>
                <FontAwesomeIcon
                  icon={faTrash}
                  className="text-white m-1"
                  title="remove content"
                  data-designer-action="delete"
                  onClick={() => deleteComponent(comp.Id)}
                  style={{ cursor: 'pointer' }} />
                <FontAwesomeIcon
                  icon={faArrowsAlt}
                  className="text-white m-1"
                  title="move content"
                  style={{ cursor: 'grab' }} />
              </div>
            </div>
            <ComponentPreview
              ApiTag={comp.ApiTag}
              ContentComponentId={comp.Id}
              VersionId={props.VersionId}
              ref={el => (previewRefs.current[`PREVIEW-${comp.Id}`] = el)}
              ContentLoaded={componentLoaded}
              IsGrid={false}
              Component={comp}
              DraftComponent={props.ComponentDrafts?.[comp.Id]}
              EditComponent={props.EditComponent}
              PropertiesOpen={props.EditingComponentId === comp.Id || props.DraftComponentIds?.includes(comp.Id)}
              OnInlineDirtyChange={props.OnInlineDirtyChange}
              SaveInlineComponent={props.SaveInlineComponent}
              pageCss={props.PageCss}
              pageClass={props.PageClass}
              sectionClass={props.SectionClass}
              sectionId={props.SectionAnchor}
              pageStyle={props.PageStyle}
              sectionStyle={props.SectionStyle}
            />
          </div>
        );
        if (comp.ColSpan > 1) {
          i += comp.ColSpan - 1;
        }
      } else {
        columns.push(
          <ColumnDropZone
            key={`col-${i}`}
            className={`${colClass} col-border text-center pt-4 pb-4`}
            columnIndex={i}
            sectionId={props.SectionId}
            onDragEnter={onColDragEnter}
            onDragLeave={onColDragLeave}
            onDragOver={onColDragOver}
            onDrop={onColComponentDrop}
          />
        );
      }
    }
    return columns;
  }

  const rowStateClass = contentComponents.length > 0
    ? "columns-section--has-content"
    : "columns-section--empty";

  return (
    <Row className={`m-0 p-0 designer-columns ${rowStateClass}`.trim()}>
      {renderColumns()}
    </Row>
  );
});

export default ColumnsTemplate;
